import React from 'react';

// Nav — react-bootstrap-compatible: <Nav variant="tabs|pills" activeKey onSelect>
// with <Nav.Item> and <Nav.Link eventKey active disabled onClick>.
interface NavCtx {
  activeKey?: string;
  onSelect?: (key: string | null) => void;
  variant?: 'tabs' | 'pills';
}
const Ctx = React.createContext<NavCtx>({});

export interface NavProps extends Omit<React.HTMLAttributes<HTMLElement>, 'onSelect'> {
  variant?: 'tabs' | 'pills';
  activeKey?: string;
  onSelect?: (key: string | null) => void;
  as?: React.ElementType;
}
interface NavType extends React.FC<NavProps> {
  Item: React.FC<React.HTMLAttributes<HTMLDivElement>>;
  Link: React.FC<React.AnchorHTMLAttributes<HTMLAnchorElement> & { eventKey?: string; active?: boolean; disabled?: boolean; as?: React.ElementType; to?: string }>;
}

export const Nav = (({ variant, activeKey, onSelect, className = '', children, ...rest }: NavProps) => (
  <Ctx.Provider value={{ activeKey, onSelect, variant }}>
    <nav className={`tw-flex tw-items-center tw-gap-1 ${variant === 'tabs' ? 'tw-border-b tw-border-line' : ''} ${className}`} {...rest}>
      {children}
    </nav>
  </Ctx.Provider>
)) as NavType;

Nav.Item = ({ className = '', children, ...rest }) => (
  <div className={`tw-inline-flex ${className}`} {...rest}>{children}</div>
);

Nav.Link = ({ eventKey, active, disabled, as, className = '', onClick, children, ...rest }) => {
  const { activeKey, onSelect, variant } = React.useContext(Ctx);
  const isActive = active ?? (eventKey !== undefined && eventKey === activeKey);
  const base = 'tw-cursor-pointer tw-whitespace-nowrap tw-px-2.5 tw-py-2 tw-transition disabled:tw-opacity-50';
  const style =
    variant === 'pills'
      ? isActive ? 'tw-rounded tw-bg-primary tw-text-white' : 'tw-rounded tw-text-content-secondary hover:tw-bg-surface-hover'
      : variant === 'tabs'
      ? isActive ? '-tw-mb-px tw-border-b-2 tw-border-primary tw-font-medium tw-text-primary' : '-tw-mb-px tw-border-b-2 tw-border-transparent tw-text-content-secondary hover:tw-text-content'
      : isActive ? 'tw-text-primary tw-font-medium' : 'tw-text-content-secondary hover:tw-text-content';
  const Comp: React.ElementType = as || 'a';
  return (
    <Comp
      className={`${base} ${style} ${className}`}
      aria-disabled={disabled}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        if (disabled) { e.preventDefault(); return; }
        onClick?.(e);
        if (eventKey !== undefined) onSelect?.(eventKey);
      }}
      {...rest}
    >
      {children}
    </Comp>
  );
};

export default Nav;
